// Chemin : C:\gestion-copro\src\services\conversation.service.ts
// Session 97 : Persistance des conversations SENTINELLE
// Session 140 : Ajout sérialisation suggestionsReponse pour persistance complète
// CRUD conversations dans coproprietes/{id}/conversations

import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { ChatMessage } from '../types/chat.types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Message sérialisé pour Firestore
 * Les dates sont converties en Timestamp Firestore
 * Session 140 : ajout suggestionsReponse
 */
interface FirestoreMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Timestamp;
  isDocumentAnalysis?: boolean;
  actions?: unknown[];
  incitationPieces?: string;
  suggestionsReponse?: string[];
}

/**
 * Document conversation dans Firestore
 */
export interface ConversationDoc {
  id: string;
  userId: string;
  titre: string;
  messages: FirestoreMessage[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convertit un ChatMessage en format Firestore
 * Session 140 : ajout suggestionsReponse
 */
function messageToFirestore(msg: ChatMessage): FirestoreMessage {
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content,
    timestamp: Timestamp.fromDate(
      msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp)
    ),
    ...(msg.isDocumentAnalysis && { isDocumentAnalysis: true }),
    ...(msg.actions && msg.actions.length > 0 && { actions: msg.actions }),
    ...(msg.incitationPieces && { incitationPieces: msg.incitationPieces }),
    ...(msg.suggestionsReponse && msg.suggestionsReponse.length > 0 && { suggestionsReponse: msg.suggestionsReponse }),
  };
}

/**
 * Convertit un message Firestore en ChatMessage
 * Session 140 : ajout suggestionsReponse
 */
function firestoreToMessage(msg: FirestoreMessage): ChatMessage {
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content,
    timestamp: msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(),
    isDocumentAnalysis: msg.isDocumentAnalysis || false,
    actions: msg.actions as ChatMessage['actions'],
    incitationPieces: msg.incitationPieces,
    suggestionsReponse: msg.suggestionsReponse,
  };
}

/**
 * Génère un titre à partir du premier message utilisateur
 * Tronqué à 60 caractères
 */
function generateTitre(messages: ChatMessage[]): string {
  const firstUserMsg = messages.find(m => m.role === 'user');
  if (!firstUserMsg) return 'Nouvelle conversation';
  
  const texte = firstUserMsg.content.trim();
  if (texte.length <= 60) return texte;
  return texte.substring(0, 57) + '...';
}

// ============================================================================
// CRUD
// ============================================================================

/**
 * Chemin de la collection conversations pour une copropriété
 */
function getConversationsRef(coproprieteId: string) {
  return collection(db, 'coproprietes', coproprieteId, 'conversations');
}

/**
 * Charge la dernière conversation active d'un utilisateur
 * Retourne null si aucune conversation trouvée
 */
export async function getActiveConversation(
  coproprieteId: string, 
  userId: string
): Promise<ConversationDoc | null> {
  try {
    const ref = getConversationsRef(coproprieteId);
    const q = query(
      ref,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const docSnap = snapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as ConversationDoc;
  } catch (error) {
    console.error('Erreur chargement conversation active:', error);
    return null;
  }
}

/**
 * Crée une nouvelle conversation
 * Retourne l'ID du document créé
 */
export async function createConversation(
  coproprieteId: string,
  userId: string,
  messages: ChatMessage[]
): Promise<string> {
  const ref = getConversationsRef(coproprieteId);
  
  const data = {
    userId,
    titre: generateTitre(messages),
    messages: messages.map(messageToFirestore),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(ref, data);
  return docRef.id;
}

/**
 * Met à jour les messages d'une conversation existante
 */
export async function updateConversationMessages(
  coproprieteId: string,
  conversationId: string,
  messages: ChatMessage[]
): Promise<void> {
  const ref = doc(db, 'coproprietes', coproprieteId, 'conversations', conversationId);
  
  await updateDoc(ref, {
    messages: messages.map(messageToFirestore),
    titre: generateTitre(messages),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Supprime une conversation
 */
export async function deleteConversation(
  coproprieteId: string,
  conversationId: string
): Promise<void> {
  const ref = doc(db, 'coproprietes', coproprieteId, 'conversations', conversationId);
  await deleteDoc(ref);
}

/**
 * Convertit les messages Firestore en ChatMessage[]
 * Utilisé lors du chargement d'une conversation
 */
export function convertFirestoreMessages(firestoreMessages: FirestoreMessage[]): ChatMessage[] {
  if (!firestoreMessages || !Array.isArray(firestoreMessages)) return [];
  return firestoreMessages.map(firestoreToMessage);
}
